import React, { useState, useEffect } from 'react';
import { Users, Mail, MessageSquare, BookOpen, BarChart3, Settings, Eye, Trash2, CheckCircle, Clock, LogOut, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminPage = () => {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [subscriptions, setSubscriptions] = useState([]);
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSubscriptions: 0,
    pendingPrayerRequests: 0,
    pendingContactSubmissions: 0,
    totalViews: 0
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  const tabs = [
    { id: 'overview', name: 'Overview', icon: BarChart3 },
    { id: 'rsvps', name: 'RSVPs', icon: Users },
    { id: 'subscriptions', name: 'Subscriptions', icon: Users },
    { id: 'prayers', name: 'Prayer Requests', icon: MessageSquare },
    { id: 'contacts', name: 'Contact Forms', icon: Mail },
    { id: 'settings', name: 'Settings', icon: Settings }
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      const [subsResponse, prayersResponse, contactsResponse, rsvpsResponse] = await Promise.all([
        fetch(`${API}/subscriptions`),
        fetch(`${API}/prayer-requests`),
        fetch(`${API}/contact-submissions`),
        fetch(`${API}/rsvps`)
      ]);

      const subscriptionsData = await subsResponse.json();
      const prayersData = await prayersResponse.json();
      const contactsData = await contactsResponse.json();
      const rsvpsData = await rsvpsResponse.json();

      setSubscriptions(subscriptionsData);
      setPrayerRequests(prayersData);
      setContactSubmissions(contactsData);
      setRsvps(rsvpsData);

      const churchRsvps = rsvpsData.filter(r => r.source !== 'dbu-campus');
      const dbuRsvps = rsvpsData.filter(r => r.source === 'dbu-campus');

      // Calculate stats
      setStats({
        totalSubscriptions: subscriptionsData.length,
        pendingPrayerRequests: prayersData.filter(p => p.status === 'pending').length,
        pendingContactSubmissions: contactsData.filter(c => c.status === 'pending').length,
        totalRsvps: rsvpsData.length,
        churchRsvps: churchRsvps.length,
        dbuRsvps: dbuRsvps.length,
      });

    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-[#f17b21] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 font-sans">Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0 min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 lg:py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2 font-display">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 font-sans">
              Manage your ministry content and community engagement
            </p>
          </div>
          
          {/* User Info & Logout */}
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="font-semibold text-gray-800 font-sans">{user?.primaryEmail || 'Admin'}</p>
              <p className="text-sm text-gray-600 font-sans">Administrator</p>
            </div>
            <button
              onClick={signOut}
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition-shadow" onClick={() => setActiveTab('rsvps')}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Total RSVPs</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.totalRsvps || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Church: {stats.churchRsvps || 0} &bull; DBU: {stats.dbuRsvps || 0}</p>
              </div>
              <Users className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Subscriptions</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.totalSubscriptions}</p>
              </div>
              <Mail className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Pending Prayers</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.pendingPrayerRequests}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-sans">Pending Messages</p>
                <p className="text-2xl font-bold text-gray-800 font-display">{stats.pendingContactSubmissions}</p>
              </div>
              <Mail className="w-8 h-8 text-[#f17b21]" />
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-lg mb-8">
          <div className="border-b border-gray-100">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 px-6 py-4 font-semibold transition-colors duration-300 font-sans whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-[#f17b21] border-b-2 border-[#f17b21]'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6 lg:p-8">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Ministry Dashboard Overview</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-bold text-gray-800 mb-4 font-sans">Recent Activity</h4>
                    <div className="space-y-3">
                      {prayerRequests.slice(0, 3).map((prayer) => (
                        <div key={prayer.id} className="flex items-center space-x-3">
                          <MessageSquare className="w-4 h-4 text-[#f17b21]" />
                          <div className="flex-1">
                            <p className="text-sm font-sans">New prayer request from {prayer.name || 'Anonymous'}</p>
                            <p className="text-xs text-gray-500 font-sans">{formatDate(prayer.submitted_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-bold text-gray-800 mb-4 font-sans">Quick Actions</h4>
                    <div className="space-y-3">
                      <button 
                        onClick={() => setActiveTab('prayers')}
                        className="w-full text-left bg-white p-3 rounded-lg hover:shadow-md transition-shadow duration-300"
                      >
                        <p className="font-semibold text-gray-800 font-sans">Review Prayer Requests</p>
                        <p className="text-sm text-gray-600 font-sans">{stats.pendingPrayerRequests} pending</p>
                      </button>
                      <button 
                        onClick={() => setActiveTab('subscriptions')}
                        className="w-full text-left bg-white p-3 rounded-lg hover:shadow-md transition-shadow duration-300"
                      >
                        <p className="font-semibold text-gray-800 font-sans">View Subscriptions</p>
                        <p className="text-sm text-gray-600 font-sans">{stats.totalSubscriptions} total</p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* RSVPs Tab */}
            {activeTab === 'rsvps' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-800 font-display">RSVPs</h3>
                  <p className="text-sm text-gray-500">{rsvps.length} total</p>
                </div>

                {/* Church Visit RSVPs */}
                <div className="mb-8">
                  <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2 font-sans">
                    <span className="w-2 h-2 bg-[#f17b21] rounded-full"></span>
                    Church Visit RSVPs ({rsvps.filter(r => r.source !== 'dbu-campus').length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Name</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Email</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Phone</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Guests</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Message</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rsvps.filter(r => r.source !== 'dbu-campus').length === 0 ? (
                          <tr><td colSpan="6" className="py-6 text-center text-gray-400">No church visit RSVPs yet</td></tr>
                        ) : rsvps.filter(r => r.source !== 'dbu-campus').map((r) => (
                          <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2.5 px-3 font-medium">{r.name}</td>
                            <td className="py-2.5 px-3">{r.email}</td>
                            <td className="py-2.5 px-3">{r.phone || '–'}</td>
                            <td className="py-2.5 px-3">{r.guest_count}</td>
                            <td className="py-2.5 px-3 max-w-[200px] truncate">{r.message || '–'}</td>
                            <td className="py-2.5 px-3 text-gray-500">{formatDate(r.submitted_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* DBU Campus RSVPs */}
                <div>
                  <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2 font-sans">
                    <span className="w-2 h-2 bg-[#003D79] rounded-full"></span>
                    DBU Campus RSVPs ({rsvps.filter(r => r.source === 'dbu-campus').length})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Name</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Email</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Phone</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Year</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Guests</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">How Heard</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Message</th>
                          <th className="text-left py-2 px-3 font-semibold text-gray-700">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rsvps.filter(r => r.source === 'dbu-campus').length === 0 ? (
                          <tr><td colSpan="8" className="py-6 text-center text-gray-400">No DBU campus RSVPs yet</td></tr>
                        ) : rsvps.filter(r => r.source === 'dbu-campus').map((r) => (
                          <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                            <td className="py-2.5 px-3 font-medium">{r.name}</td>
                            <td className="py-2.5 px-3">{r.email}</td>
                            <td className="py-2.5 px-3">{r.phone || '–'}</td>
                            <td className="py-2.5 px-3">
                              {r.year_of_study ? (
                                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">{r.year_of_study}</span>
                              ) : '–'}
                            </td>
                            <td className="py-2.5 px-3">{r.guest_count}</td>
                            <td className="py-2.5 px-3">{r.how_heard || '–'}</td>
                            <td className="py-2.5 px-3 max-w-[150px] truncate">{r.message || '–'}</td>
                            <td className="py-2.5 px-3 text-gray-500">{formatDate(r.submitted_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Subscriptions Tab */}
            {activeTab === 'subscriptions' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Email Subscriptions</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Type</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Subscribed</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-800 font-sans">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => (
                        <tr key={sub.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-sans">{sub.email}</td>
                          <td className="py-3 px-4 font-sans">{sub.name || 'Not provided'}</td>
                          <td className="py-3 px-4 font-sans">{sub.subscription_type}</td>
                          <td className="py-3 px-4 font-sans">{formatDate(sub.subscribed_at)}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold font-sans ${
                              sub.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {sub.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Prayer Requests Tab */}
            {activeTab === 'prayers' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Prayer Requests</h3>
                <div className="space-y-6">
                  {prayerRequests.map((prayer) => (
                    <div key={prayer.id} className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-gray-800 font-sans">
                            {prayer.name || 'Anonymous Request'}
                          </h4>
                          <p className="text-sm text-gray-500 font-sans">
                            {formatDate(prayer.submitted_at)}
                            {prayer.email && ` • ${prayer.email}`}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {prayer.is_urgent && (
                            <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-semibold font-sans">
                              Urgent
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold font-sans ${
                            prayer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            prayer.status === 'prayed_for' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {prayer.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-700 mb-4 font-sans leading-relaxed">
                        {prayer.request_text}
                      </p>
                      <div className="flex space-x-3">
                        <button className="bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans">
                          Mark as Prayed For
                        </button>
                        <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans">
                          Add Notes
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Submissions Tab */}
            {activeTab === 'contacts' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Contact Form Submissions</h3>
                <div className="space-y-6">
                  {contactSubmissions.map((contact) => (
                    <div key={contact.id} className="bg-white rounded-xl border border-gray-200 p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h4 className="font-bold text-gray-800 font-sans">{contact.name}</h4>
                          <p className="text-sm text-gray-500 font-sans">
                            {contact.email} • {formatDate(contact.submitted_at)}
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold font-sans ${
                          contact.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {contact.status.toUpperCase()}
                        </span>
                      </div>
                      <h5 className="font-semibold text-gray-800 mb-2 font-sans">Subject: {contact.subject}</h5>
                      <p className="text-gray-700 mb-4 font-sans leading-relaxed">
                        {contact.message}
                      </p>
                      <div className="flex space-x-3">
                        <button className="bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans">
                          Reply via Email
                        </button>
                        <button className="border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans">
                          Mark as Responded
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content Management Tab */}
            {activeTab === 'content' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">Content Management</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                    <BookOpen className="w-12 h-12 text-[#f17b21] mb-4" />
                    <h4 className="font-bold text-gray-800 mb-2 font-sans">Blog Posts</h4>
                    <p className="text-gray-600 text-sm mb-4 font-sans">Create and manage blog content</p>
                    <button className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans">
                      Manage Blog
                    </button>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                    <Users className="w-12 h-12 text-[#f17b21] mb-4" />
                    <h4 className="font-bold text-gray-800 mb-2 font-sans">YouTube Content</h4>
                    <p className="text-gray-600 text-sm mb-4 font-sans">View and organize video content</p>
                    <button className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans">
                      Manage Videos
                    </button>
                  </div>
                  
                  <div className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                    <Settings className="w-12 h-12 text-[#f17b21] mb-4" />
                    <h4 className="font-bold text-gray-800 mb-2 font-sans">Audio Content</h4>
                    <p className="text-gray-600 text-sm mb-4 font-sans">Upload and manage audio files</p>
                    <button className="w-full bg-[#f17b21] hover:bg-[#d86919] text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-300 font-sans">
                      Coming Soon
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-6 font-display">System Settings</h3>
                <div className="space-y-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h4 className="font-bold text-gray-800 mb-4 font-sans">Database Status</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-sans">Neon DB Connected</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-sans">YouTube API Active</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="font-sans">Google Analytics Tracking</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Clock className="w-5 h-5 text-yellow-500" />
                        <span className="font-sans">WordPress Blog (Coming Soon)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;